<template>
  <div class="lp-page guides-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ isBg ? 'Уроци' : 'Tutorials' }}</span>
        <h1 class="lp-title">{{ isBg ? 'Видео Уроци' : 'Video Tutorials' }}</h1>
        <p class="lp-lead">{{ isBg ? 'Научете как да използвате Invoice Scanner с нашите подробни видео уроци' : 'Learn how to use Invoice Scanner with our comprehensive video tutorials' }}</p>
      </div>
    </section>

    <!-- Tutorials -->
    <section class="lp-section">
      <div class="lp-container">
        <!-- Search + filters -->
        <div class="guides-toolbar reveal-item">
          <div class="search-box">
            <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="search-icon" />
            <input v-model="searchQuery" type="text" :placeholder="isBg ? 'Търсете видео уроци...' : 'Search video tutorials...'" />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search"><font-awesome-icon :icon="['fas', 'circle-xmark']" /></button>
          </div>
          <div class="filters">
            <button v-for="c in categories" :key="c.id" :class="['filter-pill', { active: selectedCategory === c.id }]" @click="selectedCategory = c.id">
              {{ isBg ? c.bg : c.en }}
            </button>
          </div>
        </div>

        <div class="lp-grid lp-grid-3">
          <div class="tutorial-card lp-card" v-for="(t, i) in filtered" :key="t.title.en">
            <div class="video-thumb" :class="'g' + ((i % 6) + 1)">
              <font-awesome-icon :icon="['fas', 'circle-play']" class="play-icon" />
              <span class="duration">{{ t.duration }}</span>
            </div>
            <div class="tutorial-body">
              <span class="tutorial-cat">{{ isBg ? t.catLabel.bg : t.catLabel.en }}</span>
              <h3 class="lp-h3">{{ isBg ? t.title.bg : t.title.en }}</h3>
              <p class="lp-text">{{ isBg ? t.desc.bg : t.desc.en }}</p>
              <a href="#" class="watch-link">{{ isBg ? 'Гледай Урок' : 'Watch Tutorial' }} <font-awesome-icon :icon="['fas', 'arrow-right']" /></a>
            </div>
          </div>
        </div>
        <p v-if="!filtered.length" class="no-results">{{ isBg ? 'Няма намерени уроци.' : 'No tutorials found.' }}</p>
      </div>
    </section>

    <!-- CTA -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-cta">
          <h2>{{ isBg ? 'Готови да Започнете?' : 'Ready to Get Started?' }}</h2>
          <p>{{ isBg ? 'Започнете да използвате Invoice Scanner днес и автоматизирайте обработката на вашите фактури' : 'Start using Invoice Scanner today and automate your invoice processing' }}</p>
          <div class="cta-buttons">
            <a href="/register" class="lp-btn lp-btn-primary">{{ isBg ? 'Започнете Безплатно' : 'Start Free Trial' }}</a>
            <a href="/contact" class="lp-btn lp-btn-secondary cta-secondary">{{ isBg ? 'Свържете се с нас' : 'Contact Sales' }}</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Navbar from '../components/Navbar.vue';
import Footer from '../components/Footer.vue';
import { useReveal } from '../composables/useReveal';

const { locale } = useI18n();
const isBg = computed(() => locale.value === 'bg');
const searchQuery = ref('');
const selectedCategory = ref('all');

const categories = [
  { id: 'all', bg: 'Всички', en: 'All' },
  { id: 'getting-started', bg: 'Начало', en: 'Getting Started' },
  { id: 'basics', bg: 'Основи', en: 'Basics' },
  { id: 'advanced', bg: 'Напреднали', en: 'Advanced' },
  { id: 'features', bg: 'Функции', en: 'Features' },
];

const tutorials = [
  { cat: 'getting-started', duration: '5:30', catLabel: { bg: 'Начало', en: 'Getting Started' }, title: { bg: 'Първи Стъпки с Invoice Scanner', en: 'Getting Started with Invoice Scanner' }, desc: { bg: 'Научете основите на сканиране и обработка на фактури', en: 'Learn the basics of scanning and processing invoices' } },
  { cat: 'basics', duration: '8:45', catLabel: { bg: 'Основи', en: 'Basics' }, title: { bg: 'Как да Сканирате Фактури', en: 'How to Scan Invoices' }, desc: { bg: 'Пълно ръководство за сканиране и качване на документи', en: 'Complete guide to scanning and uploading documents' } },
  { cat: 'advanced', duration: '12:20', catLabel: { bg: 'Напреднали', en: 'Advanced' }, title: { bg: 'Извличане и Валидация на Данни', en: 'Data Extraction and Validation' }, desc: { bg: 'Научете как да извличате и валидирате данни от фактури', en: 'Learn how to extract and validate invoice data' } },
  { cat: 'features', duration: '6:15', catLabel: { bg: 'Функции', en: 'Features' }, title: { bg: 'Експорт във Различни Формати', en: 'Exporting to Different Formats' }, desc: { bg: 'Научете как да експортирате данни в NAP Sales, Excel, PDF и други', en: 'Learn how to export data to NAP Sales, Excel, PDF and more' } },
  { cat: 'advanced', duration: '10:30', catLabel: { bg: 'Напреднали', en: 'Advanced' }, title: { bg: 'Масова Обработка на Фактури', en: 'Batch Processing Invoices' }, desc: { bg: 'Научете как да обработвате множество фактури наведнъж', en: 'Learn how to process multiple invoices at once' } },
  { cat: 'features', duration: '15:40', catLabel: { bg: 'Разработчици', en: 'Developers' }, title: { bg: 'API Интеграция', en: 'API Integration' }, desc: { bg: 'Интегрирайте Invoice Scanner във вашето приложение', en: 'Integrate Invoice Scanner into your application' } },
  { cat: 'basics', duration: '9:10', catLabel: { bg: 'Съвети', en: 'Tips' }, title: { bg: 'Съвети и Трикове за Ефективност', en: 'Tips & Tricks for Efficiency' }, desc: { bg: 'Научете най-добрите практики и как да работите по-бързо', en: 'Learn best practices and how to work faster' } },
  { cat: 'getting-started', duration: '7:25', catLabel: { bg: 'Помощ', en: 'Support' }, title: { bg: 'Решаване на Проблеми', en: 'Troubleshooting Common Issues' }, desc: { bg: 'Намерете решения на често срещани проблеми', en: 'Find solutions to common problems' } },
  { cat: 'features', duration: '11:00', catLabel: { bg: 'Мобилно', en: 'Mobile' }, title: { bg: 'Използване на Мобилното Приложение', en: 'Using the Mobile App' }, desc: { bg: 'Научете как да сканирате фактури на ход', en: 'Learn how to scan invoices on the go' } },
];

const filtered = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return tutorials.filter((t) => {
    const catOk = selectedCategory.value === 'all' || t.cat === selectedCategory.value;
    if (!catOk) return false;
    if (!q) return true;
    const text = (isBg.value ? t.title.bg + t.desc.bg : t.title.en + t.desc.en).toLowerCase();
    return text.includes(q);
  });
});

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

useReveal();
</script>

<style scoped>
.guides-toolbar { margin-bottom: 40px; display: flex; flex-direction: column; gap: 18px; align-items: center; }
.search-box { position: relative; width: 100%; max-width: 520px; }
.search-box input { width: 100%; padding: 14px 44px; border: 1px solid var(--line); border-radius: var(--pill); background: #fff; color: var(--ink); font-size: 15px; font-family: inherit; transition: border-color 0.2s ease, box-shadow 0.2s ease; }
.search-box input:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(43,130,240,0.12); }
.search-icon { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: var(--muted); }
.clear-search { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted); cursor: pointer; font-size: 16px; }
.filters { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.filter-pill { padding: 9px 18px; border-radius: var(--pill); border: 1px solid var(--line); background: #fff; color: var(--muted); font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
.filter-pill:hover { border-color: var(--violet); color: var(--violet); }
.filter-pill.active { background: var(--violet); border-color: var(--violet); color: #fff; }

.tutorial-card { padding: 0; overflow: hidden; display: flex; flex-direction: column; }
.video-thumb { height: 150px; position: relative; display: flex; align-items: center; justify-content: center; }
.video-thumb.g1 { background: linear-gradient(150deg, #5aa2ff, #2b82f0); }
.video-thumb.g2 { background: linear-gradient(150deg, #34d399, #10b981); }
.video-thumb.g3 { background: linear-gradient(150deg, #a78bfa, #7c5cff); }
.video-thumb.g4 { background: linear-gradient(150deg, #fbbf24, #f59e0b); }
.video-thumb.g5 { background: linear-gradient(150deg, #22d3ee, #06b6d4); }
.video-thumb.g6 { background: linear-gradient(150deg, #f472b6, #ec4899); }
.play-icon { font-size: 46px; color: rgba(255,255,255,0.92); }
.duration { position: absolute; bottom: 12px; right: 12px; background: rgba(0,0,0,0.55); color: #fff; font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: var(--pill); }
.tutorial-body { padding: 22px 26px 26px; display: flex; flex-direction: column; flex: 1; }
.tutorial-cat { color: var(--violet); font-weight: 700; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px; }
.tutorial-card .lp-h3 { font-size: 18px; margin-bottom: 8px; line-height: 1.25; }
.tutorial-card .lp-text { flex: 1; margin-bottom: 14px; font-size: 14px; }
.watch-link { display: inline-flex; align-items: center; gap: 8px; color: var(--violet); font-weight: 600; font-size: 14px; transition: gap 0.2s ease; }
.tutorial-card:hover .watch-link { gap: 12px; }
.no-results { text-align: center; color: var(--muted); padding: 40px 0; }

.cta-buttons { display: inline-flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
.cta-secondary { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); }
.cta-secondary:hover { background: rgba(255,255,255,0.18); color: #fff; border-color: rgba(255,255,255,0.4); }
</style>
